import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { toApiError } from "src/types/api";
// Define interfaces for workflow step and full response
export interface WorkflowStep {
    step: string;
    name: string;
    status: string;
    message: string | { en: string; ko: string };
    duration: string;
    detail?: {
        progress_percent?: number;
        remaining_seconds?: number;
        remaining_text?: string;
        current_molecule_num?: number;
        user_input_molecule_num?: number;
    };
    consistency?: {
        index: string;
        trace: string;
    };
}

export interface WorkflowResponse {
    workflow_name: string;
    round: number;
    workflow_key: string;
    workflow_id: string;
    batch_status: string;
    overall_status: string;
    steps: WorkflowStep[];
    Message: string;
    backendMessage?: string;
    failedStepsDetails?: {
        count: number;
        steps: WorkflowStep[];
        summary: string;
    };
    group_meta: {
        [key: string]: any;
    }
}

// Error response interface for 400+ errors
export interface ErrorResponse {
    error: string;
    message: string;
    [key: string]: any; // Additional fields that might come from the API
}

export interface WorkflowStatusState {
    response: WorkflowResponse | null;
    isLoading: boolean;
    error: string | null;
    errorResponse: ErrorResponse | null; // Add error response object
    failedSteps: WorkflowStep[]; // Store failed steps for easy access
    activeRequestKey: string | null; // 현재 화면이 기다리는 요청을 식별. 다른 라이브러리로 이동한 뒤 늦게 도착한 응답을 무시하기 위함
}

const initialState: WorkflowStatusState = {
    response: null,
    isLoading: false,
    error: null,
    errorResponse: null, // Initialize error response
    failedSteps: [],
    activeRequestKey: null,
};

const buildRequestKey = (data: { group_name: string; library_name: string; round: number; workflow_name: string }) =>
    `${data.group_name}:${data.library_name}:${data.round}:${data.workflow_name}`;

// Helper function to extract message text
const extractMessageText = (message: string | { en: string; ko: string }, language: 'en' | 'ko' = 'en'): string => {
    if (typeof message === 'string') {
        return message;
    }
    return message[language] || message.en || 'Unknown error';
};

// Helper function to create comprehensive message
const createComprehensiveMessage = (response: WorkflowResponse | null): string => {
    if (!response) return "No workflow data available";
    
    const { batch_status, overall_status, steps } = response;
    
    if (!steps || steps.length === 0) {
        return `Workflow Status: ${overall_status} - No steps data available`;
    }
    
    const failedSteps = steps.filter((step: WorkflowStep) => 
        step.status === "FAILED" || step.status === "Failed"
    );
    const completedSteps = steps.filter((step: WorkflowStep) => 
        step.status === "COMPLETED" || step.status === "Completed"
    );
    
    let message = `Workflow Status: ${overall_status}`;
    
    if (batch_status === "FAILED") {
        message += `\n\n Batch execution failed with ${failedSteps.length} failed step(s) out of ${steps.length} total steps.`;
        
        if (completedSteps.length > 0) {
            message += `\n\nSuccessfully completed steps:`;
            completedSteps.forEach((step: WorkflowStep) => {
                message += `\n  • ${step.name} (${step.duration})`;
            });
        }
        
        if (failedSteps.length > 0) {
            message += `\n\n Failed steps:`;
            failedSteps.forEach((step: WorkflowStep) => {
                const errorMessage = extractMessageText(step.message);
                message += `\n  • ${step.name}: ${errorMessage}`;
                if (step.duration && step.duration !== "0.0") {
                    message += ` (Duration: ${step.duration})`;
                }
            });
        }
    } else {
        message += `\n\n All ${completedSteps.length} steps completed successfully.`;
        completedSteps.forEach((step: WorkflowStep) => {
            message += `\n  • ${step.name} (${step.duration})`;
        });
    }
    
    return message;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const workFlowStatusAPI = createAsyncThunk(
    "workFlowStatusAPI",
    async (data: {
        group_name: string;
        library_name: string;
        round: number;
        workflow_name: string;
    }, { rejectWithValue }) => {
        const djangoEndpoint = getDjangoEndpoint();
        const pathParam = '/workflow/status/';
        const requestPayload = {
            workflow_name: data.workflow_name,
            group_name: data.group_name,
            library_name: data.library_name,
            round: data.round,
        };

        try {
            const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestPayload);
            return response.data;
        } catch (caughtError) {
            let error: unknown = caughtError;
            if (toApiError(error).response?.status === 500) {
                try {
                    await delay(3000);
                    const retryResponse = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestPayload);
                    return retryResponse.data;
                } catch (retryError) {
                    error = retryError;
                }
            }

            // Enhanced error handling to capture the full error response
            const apiError = toApiError(error);
            const errorData = {
                message: apiError.response?.data?.message || "Failed to fetch workflow status",
                response: apiError.response?.data || null,
                status: apiError.response?.status || null
            };
            return rejectWithValue(errorData);
        }
    }
);

export const workFlowStatusSlice = createSlice({
    name: "workFlowStatus",
    initialState,
    reducers: {
        clearWorkflowStatus: (state) => {
            state.response = null;
            state.isLoading = false;
            state.error = null;
            state.errorResponse = null; // Clear error response as well
            state.failedSteps = [];
            state.activeRequestKey = null;
        },
        // Add action to clear only error states
        clearWorkflowError: (state) => {
            state.error = null;
            state.errorResponse = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(workFlowStatusAPI.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.errorResponse = null; // Clear previous error response
                state.activeRequestKey = buildRequestKey(action.meta.arg);
            })
            .addCase(workFlowStatusAPI.fulfilled, (state, action) => {
                state.isLoading = false;

                // 요청 이후 다른 라이브러리로 이동했다면, 늦게 도착한 이 응답은 지금 화면과 무관하므로 무시
                if (state.activeRequestKey !== buildRequestKey(action.meta.arg)) {
                    return;
                }

                state.error = null;
                state.errorResponse = null; // Clear error response on success

                // Safely extract failed steps with proper status checking
                const failedSteps = action.payload?.steps?.filter(
                    (step: WorkflowStep) => step.status === "FAILED" || step.status === "Failed"
                ) || [];

                const backendMessage =
                    typeof action.payload?.Message === "string"
                        ? action.payload.Message
                        : typeof action.payload?.message === "string"
                            ? action.payload.message
                            : undefined;

                // Create enhanced response with comprehensive message and failed steps details
                const enhancedResponse: WorkflowResponse = {
                    ...action.payload,
                    backendMessage,
                    Message: action.payload ? createComprehensiveMessage(action.payload) : "No workflow data available",
                    failedStepsDetails: {
                        count: failedSteps.length,
                        steps: failedSteps,
                        summary: failedSteps.length > 0
                            ? `${failedSteps.length} step(s) failed: ${failedSteps.map((step: WorkflowStep) => step.name).join(', ')}`
                            : 'All steps completed successfully'
                    }
                };

                state.response = enhancedResponse;
                state.failedSteps = failedSteps;
            })
            .addCase(workFlowStatusAPI.rejected, (state, action) => {
                state.isLoading = false;

                // 요청 이후 다른 라이브러리로 이동했다면, 늦게 도착한 이 에러도 지금 화면과 무관하므로 무시
                if (state.activeRequestKey !== buildRequestKey(action.meta.arg)) {
                    return;
                }

                // Enhanced error handling with response data
                const payload = action.payload as any;

                // Set the basic error message
                state.error = payload?.message || "Unknown error occurred";

                // Store the full error response if available
                if (payload?.response) {
                    state.errorResponse = payload.response;
                }

                // Clear successful response data on error
                state.response = null;
                state.failedSteps = [];
            });
    }
});

export const { clearWorkflowStatus, clearWorkflowError } = workFlowStatusSlice.actions;

// Enhanced selectors
export const selectWorkflowStatus = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.response;
export const selectFailedSteps = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.failedSteps;
export const selectIsWorkflowLoading = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.isLoading;
export const selectWorkflowError = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.error;
export const selectWorkflowErrorResponse = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.errorResponse; // New selector for error response

// New selectors for enhanced functionality
export const selectWorkflowMessage = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.response?.Message;
export const selectFailedStepsDetails = (state: { workFlowStatus: WorkflowStatusState }) => state.workFlowStatus.response?.failedStepsDetails;
export const selectCompletedSteps = (state: { workFlowStatus: WorkflowStatusState }) => {
    return state.workFlowStatus.response?.steps?.filter(step => 
        step.status === "COMPLETED" || step.status === "Completed"
    ) || [];
};
export const selectWorkflowSummary = (state: { workFlowStatus: WorkflowStatusState }) => {
    const response = state.workFlowStatus.response;
    if (!response) return null;
    
    const totalSteps = response.steps?.length || 0;
    const completedSteps = response.steps?.filter(step => 
        step.status === "COMPLETED" || step.status === "Completed"
    )?.length || 0;
    const failedSteps = response.steps?.filter(step => 
        step.status === "FAILED" || step.status === "Failed"
    )?.length || 0;
    
    return {
        total: totalSteps,
        completed: completedSteps,
        failed: failedSteps,
        status: response.overall_status,
        success: failedSteps === 0 && completedSteps > 0
    };
};

// Additional utility selector to get error details for specific steps
export const selectStepErrorDetails = (state: { workFlowStatus: WorkflowStatusState }, stepIds: string[]) => {
    const response = state.workFlowStatus.response;
    if (!response?.steps) return [];
    
    return response.steps
        .filter(step => stepIds.includes(step.step) && (step.status === "FAILED" || step.status === "Failed"))
        .map(step => ({
            step: step.step,
            name: step.name,
            message: extractMessageText(step.message),
            originalMessage: step.message,
            duration: step.duration
        }));
};

// New utility selectors for error handling
export const selectIsError = (state: { workFlowStatus: WorkflowStatusState }) => {
    return !!state.workFlowStatus.error || !!state.workFlowStatus.errorResponse;
};

export const selectErrorDetails = (state: { workFlowStatus: WorkflowStatusState }) => {
    const errorResponse = state.workFlowStatus.errorResponse;
    const error = state.workFlowStatus.error;
    
    return {
        hasError: !!error || !!errorResponse,
        message: error,
        errorType: errorResponse?.error,
        errorMessage: errorResponse?.message,
        fullResponse: errorResponse
    };
};

export default workFlowStatusSlice.reducer;
